import * as i18n from "../i18n.ts";
import { PIECE, registerUnit, UnitRegistry } from "./ingredientParser.ts";

let formatter: Intl.NumberFormat;
let formatterLanguage: string;

function getFormatter(){
  const language = i18n.getSelectedLanguage();
  if(formatterLanguage !== language){
    formatterLanguage = language;
    formatter = new Intl.NumberFormat(language, {maximumFractionDigits: 1});
  }
  return formatter;
}

/** The units offered by the quantity picker, in chip order, as units.json keys. */
const QUANTITY_UNITS = [PIECE,'g','oz','lb','pack','bag','box','bch'];

/** Bounds of a quantity, shared by the picker and the item form so that both agree. */
export const QUANTITY_MIN = 0.1, QUANTITY_MAX = 999.9, QUANTITY_STEP = 0.1;

/**
 * A typed, stepped or summed quantity brought back into range: one decimal at most, never above the
 * ceiling, and 0 for anything under the minimum since 0 is how "no quantity" is written.
 */
export function clampQuantity(quantity :number) :number {
  if(!(quantity>=QUANTITY_MIN)){
    return 0;
  }
  return Math.min(QUANTITY_MAX,Math.round(quantity*10)/10);
}

/**
 * The unit to show for a value that comes from an item: the unitKey of a unit we translate is kept
 * ("g", "cup"), the unitKey of one we do not becomes the word of the language ("btl" -> "bouteille"),
 * which is what the parser and every other client write. Anything unknown is kept as it is.
 */
export function localUnit(unit :string) :string {
  return unit ? UnitRegistry.get().output(unit,i18n.getShortLanguage()) : unit;
}

/** Units the family already uses, gathered from the items at load and from the picker itself. */
const usedUnits = new Set<string>();

/**
 * Declare a unit in use: known to the ingredient parser, and offered by the picker from now on.
 * @returns the canonical unit name to store, "grams" being the "g" we already know.
 */
export function rememberUnit(written :string, localeHint ?:string) :string {
  const unit = registerUnit(written,localeHint);
  usedUnits.add(unit);
  return unit;
}

/**
 * The chips of the quantity picker: the usual suspects, then what the family already uses, then the
 * unit of the edited item — which may come from another client and has to stay visible.
 */
export function unitChoices(current ?:string) :string[] {
  //the same unit must be written the same way whether it is picked here or parsed from a typed line,
  //otherwise the same unit gets two identities and stops merging
  const chips :string[] = [];
  for(const unit of [...QUANTITY_UNITS,...usedUnits,current]){
    const chip = localUnit(unit);
    if(chip && !chips.includes(chip)) chips.push(chip);
  }
  return chips;
}

/**
 * Standalone name of a unit, for the chips of the quantity picker: "g" -> "gr", "bch" -> "bunch" —
 * from nls/manual.js for the units we translate, from units.json for the others. Distinct from
 * units.<unit>.N, which always embeds the quantity.
 */
export function formatUnitName(unit :string) :string {
  const
    local = localUnit(unit),
    key = 'units.'+local+'.name',
    label = i18n.get(key);

  //unit written by another client that we don't know about: show it as-is rather than the raw key
  return label === key ? local : label;
}

/** The quantity alone, localized: 2.5 -> "2,5" in French. */
export function formatQuantity(quantity :number) :string {
  return getFormatter().format(quantity);
}

/**
 * The head of a quick add line: 300 + "g" -> "300g", 2 + "pc" -> "2". Canonical unit names on
 * purpose, and no localized decimal separator, so IngredientParser reads back what we wrote.
 */
export function formatQuantityPrefix(quantity :number, unit :string) :string {
  if(!(quantity>0)){
    return '';
  }
  const local = localUnit(unit);

  //a bare count needs no unit, and writing "2 pc" would be odd in the input
  return local && local!==PIECE ? quantity+local : String(quantity);
}

/**
 * Human-readable quantity for a list item: 300 + "g" -> "300g", 2 + "cup" -> "2 cups",
 * 2 + "bouteille" -> "2 bouteille", 4 + "pc" -> "4". Returns '' when there is nothing to show.
 */
export function formatQuantityUnit(quantity :number, unit :string) :string {
  if(quantity == null){
    return '';
  }
  const
    formatted = formatQuantity(quantity),
    local = localUnit(unit),
    // a bare count needs no unit: "4 onions" shows just "4"
    key = 'units.'+local;

  if(!local || local === PIECE){
    return formatted;
  }

  const label = i18n.get(key, quantity);

  //a unit we do not translate, or one written by a client we know nothing about: it is already a word
  if(label === key){
    return formatted+' '+local;
  }
  //i18n.get() picks the plural form from the number, then substitutes ${.} with that same raw
  //number — so swap in the localized one ("2.5 cups" -> "2,5 cups")
  return label.replace(String(quantity), formatted);
}
